import { cn, tv, VariantProps } from "@utils/tw"
import { Link } from "eddev/routing"
import { forwardRef, ReactElement, useContext } from "react"

export type ButtonProps = VariantProps<typeof buttonStyles> & {
  icon?: ReactElement | null
  iconAlign?: "left" | "right"
  href?: string
  target?: string
  rel?: string
  className?: string
  style?: React.CSSProperties
  outerClassName?: string
  children?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  onMouseOver?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  onMouseOut?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  type?: "button" | "submit" | "reset"
  size?: "default" | "sm"
}

export const Button = forwardRef<HTMLDivElement, ButtonProps>((props, ref) => {
  const styles = buttonStyles({ ...props, variant: props.variant })
  const { iconAlign = "left", type, outerClassName, ...restProps } = props
  const iconAlignVal = iconAlign ?? "left"
  const Element = props.href ? Link : "button"
  const elProps = props.href
    ? {
        href: props.href,
        target: props.target,
        rel: props.rel,
      }
    : {
        type: props.type ?? "button",
      }

  return (
    <div className={cn("flex", outerClassName)} ref={ref}>
      <Element {...restProps} {...elProps} className={cn(styles.base(), props.className)} style={props.style}>
        {props.icon && iconAlignVal === "left" && <span className={styles.icon()}>{props.icon}</span>}
        <span className={styles.label()}>{props.children}</span>
        {props.icon && iconAlignVal === "right" && <span className={styles.icon()}>{props.icon}</span>}
      </Element>
    </div>
  )
})

export const buttonStyles = tv({
  base: [
    "inline-flex w-fit items-center justify-center outline-none cursor-pointer no-underline",
    "transition-all duration-300",
  ],
  slots: {
    label: "",
    icon: "",
  },
  variants: {
    variant: {
      primary: "bg-slate200 text-slate950 md:hover:bg-slate300 md:hover:text-slate950",
      dark: "bg-slate950 text-slate50 md:hover:bg-slate800 md:hover:text-slate50",
      orange: "bg-orange text-slate950 md:hover:bg-orange400 md:hover:text-slate950",
      medium: "bg-slate300 text-slate950 md:hover:bg-slate400 md:hover:text-slate950",
      mediumDark: "bg-slate400 text-slate950 md:hover:bg-slate700 md:hover:text-slate50",
    },
    size: {
      // default: "px-5 py-4 md:px-4 md:py-3 type-mono-xs-regular",
      default: "px-4 py-3 md:px-3 md:py-2 type-mono-xs-regular",
      sm: "px-4 py-3 md:px-3 md:py-2 type-mono-xs-regular",
    },
    radius: {
      sm: "rounded-sm",
    },
  },
  defaultVariants: {
    variant: "primary",
    radius: "sm",
    size: "default",
  },
})
